/**
 * SectionNav — left rail navigation across the 15 canonical sections.
 *
 * For each catalog entry:
 *   - shows the effective (per-plan override-aware) title / short title
 *   - shows row count
 *   - shows category/risk + portal badges
 *   - marks the active section with a stronger background
 *   - shows a small "unsaved" dot when the section is dirty
 *   - exposes a pencil "Edit section" affordance for authorised users
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Pencil } from 'lucide-react';

const PERM_GATE_LABEL = {
  'behaviour.view': 'BEHAVIOUR',
  'careplans.eol.view': 'EOL',
};

const CATEGORY_LABEL = {
  BEHAVIOUR: 'BEHAVIOUR',
  EOL: 'EOL',
  HIGH_RISK: 'HIGH RISK',
};

function categoryBadgeText(section, entry) {
  const tag = section?.category_tag;
  if (tag === 'CUSTOM') return (section?.category_label || 'CUSTOM').toUpperCase();
  if (tag) return CATEGORY_LABEL[tag] || tag;
  if (entry.permission_gate) return PERM_GATE_LABEL[entry.permission_gate] || 'GATED';
  return null;
}

export function SectionNav({ catalog, sectionByKey, activeKey, onSelect, dirtySections, onEditSection, canEditMeta }) {
  return (
    <Card data-testid="structured-section-nav">
      <CardContent className="p-2 space-y-1">
        {catalog.map((entry) => {
          const populated = sectionByKey[entry.key];
          const rows = populated?.rows?.length || 0;
          const isActive = activeKey === entry.key;
          const isDirty = !!dirtySections?.[entry.key];
          const effTitle = populated?.short_title || populated?.title_override || entry.title;
          const catText = categoryBadgeText(populated, entry);
          const portalVisible = populated?.portal_visible != null
            ? !!populated.portal_visible
            : !!entry.portal_default;
          return (
            <div
              key={entry.key}
              className={`group w-full flex items-center rounded transition-colors ${
                isActive ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-100 text-zinc-800'
              }`}
            >
              <button
                onClick={() => onSelect(entry.key)}
                className="flex-1 min-w-0 text-left text-sm px-2 py-1.5"
                data-testid={`structured-section-nav-${entry.key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">
                    <span className="opacity-60 mr-1.5 text-[10px] font-mono">
                      {String(entry.order).padStart(2, '0')}
                    </span>
                    {effTitle}
                  </span>
                  <span className="flex items-center gap-1 shrink-0">
                    {isDirty && (
                      <span className="h-1.5 w-1.5 bg-rose-500 rounded-full" title="unsaved" />
                    )}
                    {portalVisible && (
                      <Badge variant="outline"
                             className={`text-[9px] px-1 py-0 ${isActive ? 'bg-emerald-700/40 text-emerald-100 border-emerald-500/40' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
                             data-testid={`structured-section-portal-badge-${entry.key}`}>
                        portal
                      </Badge>
                    )}
                    {catText && (
                      <Badge variant="outline"
                             className={`text-[9px] px-1 py-0 ${isActive ? 'bg-zinc-800 text-zinc-200 border-zinc-700' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
                             data-testid={`structured-section-category-badge-${entry.key}`}>
                        {catText}
                      </Badge>
                    )}
                    <Badge variant="outline"
                           className={`text-[10px] px-1 py-0 ${
                             rows > 0
                               ? (isActive ? 'bg-emerald-500/20 text-emerald-100 border-emerald-400/40' : 'bg-emerald-50 text-emerald-700 border-emerald-200')
                               : (isActive ? 'bg-zinc-700 text-zinc-300 border-zinc-600' : 'bg-zinc-50 text-zinc-500 border-zinc-200')
                           }`}>
                      {rows}
                    </Badge>
                  </span>
                </div>
              </button>
              {canEditMeta && (
                <button
                  onClick={(e) => { e.stopPropagation(); onEditSection?.(entry.key); }}
                  title="Edit section"
                  className={`px-2 py-1.5 opacity-60 hover:opacity-100 transition-opacity ${isActive ? 'text-white' : 'text-zinc-500'}`}
                  data-testid={`section-edit-btn-${entry.key}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default SectionNav;
